import { useCallback, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { Badge, Button, Card, CardBody, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap"

import { CanView } from "components/Common/CanView"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import InputField from "components/Common/InputField"
import PageWrapper from "components/Common/PageWrapper"
import TableContainer from "components/Common/TableContainer"
import { ACCESS } from "constants/accessPermissions"
import { useApproveLeaveRequest, useLeaveRequests, useRejectLeaveRequest } from "hooks/leaveHook"
import { useListParams } from "hooks/useListParams"

const STATUS_COLORS = { pending: "warning", approved: "success", rejected: "danger", cancelled: "secondary" }

const LeaveRequestList = () => {
  document.title = "Leave Requests"

  const { params, updateParams: setParams } = useListParams({ page: 1, per_page: 15 })
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [actionModal, setActionModal] = useState({ open: false, type: null, request: null })
  const [actionNote, setActionNote] = useState("")

  const { data, isLoading, refetch } = useLeaveRequests({
    ...params,
    ...(search && { search }),
    ...(statusFilter && { status: statusFilter }),
  })
  const requests = data?.data?.data ?? []
  const pagination = data?.data

  const approveMutation = useApproveLeaveRequest()
  const rejectMutation = useRejectLeaveRequest()

  const handleAction = async () => {
    if (!actionModal.request) return
    try {
      if (actionModal.type === "approve") {
        await approveMutation.mutateAsync({ id: actionModal.request.id, action_note: actionNote })
        toast.success("Leave request approved")
      } else {
        await rejectMutation.mutateAsync({ id: actionModal.request.id, action_note: actionNote })
        toast.success("Leave request rejected")
      }
      setActionModal({ open: false, type: null, request: null })
      setActionNote("")
    } catch {
      toast.error("Action failed")
    }
  }

  const columns = useMemo(
    () => [
      { id: "employee", Header: "Employee", accessor: "employee.name" },
      { id: "leave_type", Header: "Leave Type", accessor: "leave_type.name" },
      { id: "start_date", Header: "Start Date", accessor: "start_date" },
      { id: "end_date", Header: "End Date", accessor: "end_date" },
      {
        id: "days",
        Header: "Days",
        accessor: row => `${row.days}${row.is_half_day ? " (½)" : ""}`,
      },
      {
        id: "status",
        Header: "Status",
        accessor: row => (
          <Badge color={STATUS_COLORS[row.status]}>{row.status}</Badge>
        ),
      },
      {
        id: "actions",
        Header: "Actions",
        accessor: row =>
          row.status === "pending" && (
            <div className="d-flex gap-1">
              <Button
                size="sm"
                color="success"
                onClick={() => setActionModal({ open: true, type: "approve", request: row })}
              >
                Approve
              </Button>
              <Button
                size="sm"
                color="danger"
                onClick={() => setActionModal({ open: true, type: "reject", request: row })}
              >
                Reject
              </Button>
            </div>
          ),
      },
    ],
    []
  )

  return (
    <PageWrapper title="Leave Requests">
      <Card>
        <CardBody>
          <Row className="mb-3">
            <Col md={3}>
              <GlobalInputSearch
                value={search}
                onChange={setSearch}
                placeholder="Search employee..."
              />
            </Col>
            <Col md={2}>
              <select
                className="form-select"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </Col>
          </Row>
          <TableContainer
            columns={columns}
            data={requests}
            isLoading={isLoading}
            pagination={pagination}
            params={params}
            setParams={setParams}
            onRefresh={refetch}
          />
        </CardBody>
      </Card>

      <Modal isOpen={actionModal.open} toggle={() => setActionModal({ open: false, type: null, request: null })}>
        <ModalHeader>
          {actionModal.type === "approve" ? "Approve" : "Reject"} Leave Request
        </ModalHeader>
        <ModalBody>
          <InputField
            label="Note (optional)"
            type="textarea"
            value={actionNote}
            onChange={setActionNote}
            rows={3}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            color={actionModal.type === "approve" ? "success" : "danger"}
            onClick={handleAction}
            disabled={approveMutation.isPending || rejectMutation.isPending}
          >
            {actionModal.type === "approve" ? "Approve" : "Reject"}
          </Button>
          <Button color="secondary" onClick={() => setActionModal({ open: false, type: null, request: null })}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </PageWrapper>
  )
}

export default LeaveRequestList